import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {OttopilotEvent, OttopilotEventCudLog} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {LogActionType} from "~/domainModels/logs";
import {extractEventSnapshotFieldChanges} from "~/modules/activityTimeline/resolver/eventLogFieldChangeUtils";
import {resolveUtmSourceFromAdditionalColumnValues, resolveEventStatusDisplayForActivityTimeline} from "~/modules/activityTimeline/utils/activityTimelineDisplayUtils";
import {
    ActivityCardType,
    type ActivityCardCreatedBy,
    type ActivityTimelineResolverContext,
    type EventTypeActivityCardData,
    type DetailsChangeActivityCardData,
} from "~/modules/activityTimeline/types/activityTimeline.types";
import {toActivityCardCreatedBy} from "~/modules/activityTimeline/utils/activityTimelineCreatedBy";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

function toCreatedBy(userId: Uuid | null | undefined): ActivityCardCreatedBy {
    return toActivityCardCreatedBy(userId);
}

function buildEventIndexMap(events: Array<OttopilotEvent>): Map<Uuid, number> {
    const templateCounts = new Map<Uuid, number>();
    const eventIndexById = new Map<Uuid, number>();
    const sortedEvents = [...events].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const event of sortedEvents) {
        const index = templateCounts.get(event.templateId) ?? 0;
        eventIndexById.set(event.id, index);
        templateCounts.set(event.templateId, index + 1);
    }
    return eventIndexById;
}

function resolveEventFromLog(
    snapshot: OttopilotEventCudLog,
    journeyEvents: Array<OttopilotEvent>,
): OttopilotEvent {
    return journeyEvents.find((event) => event.id === snapshot.id) ?? snapshot;
}

function resolveEventUtmSource(
    event: OttopilotEvent,
    context: ActivityTimelineResolverContext,
): string | null {
    const columnIds = context.workspaceSpecific?.eventUtmSourceColumnIds ?? [];
    return resolveUtmSourceFromAdditionalColumnValues(event.additionalColumnValues, columnIds) ?? null;
}

export function resolveFromEventLogs(getAdditionalColumns: CapabilityUdoColumnResolver, context: ActivityTimelineResolverContext): Array<
    EventTypeActivityCardData | DetailsChangeActivityCardData
> {
    const cards: Array<EventTypeActivityCardData | DetailsChangeActivityCardData> = [];

    const logsByEventId = new Map<Uuid, Array<(typeof context.eventLogs)[number]>>();
    for (const log of context.eventLogs) {
        if (log.eventContainerId !== context.eventContainer.id) {
            continue;
        }
        const existing = logsByEventId.get(log.id) ?? [];
        existing.push(log);
        logsByEventId.set(log.id, existing);
    }

    const currentEventIds = new Set(context.eventContainer.events.map((event) => event.id));
    const historicalEvents = [...logsByEventId.values()]
        .flatMap((snapshots) => snapshots.slice(0, 1))
        .filter((snapshot) => !currentEventIds.has(snapshot.id));
    const eventIndexById = buildEventIndexMap([...context.eventContainer.events, ...historicalEvents]);

    for (const [eventId, eventLogs] of logsByEventId) {
        const sortedLogs = [...eventLogs].sort((left, right) => {
            const updatedAtComparison = left.updatedAt.localeCompare(right.updatedAt);
            return updatedAtComparison === 0 ? left.revisionId.localeCompare(right.revisionId) : updatedAtComparison;
        });

        for (const [index, log] of sortedLogs.entries()) {
            const previousLog = sortedLogs[index - 1];
            const event = resolveEventFromLog(log, context.eventContainer.events);
            if (previousLog === undefined) {
                const statusRaw = resolveEventStatusDisplayForActivityTimeline(getAdditionalColumns, log.templateId, log.additionalColumnValues, context);
                cards.push({
                    cardType: ActivityCardType.eventType,
                    id: log.revisionId,
                    timestamp: log.updatedAt,
                    actionType: LogActionType.create,
                    eventId: eventId,
                    eventTemplateId: log.templateId,
                    eventIndex: eventIndexById.get(eventId) ?? 0,
                    createdBy: toCreatedBy(log.updatedBy),
                    simpleText: undefined,
                    remarks: null,
                    sourceRaw: resolveEventUtmSource(log, context),
                    statusRaw: statusRaw ?? null,
                });
                continue;
            }

            const fieldChanges = extractEventSnapshotFieldChanges(getAdditionalColumns, previousLog, log, context);
            if (fieldChanges.length > 0) {
                cards.push({
                    cardType: ActivityCardType.detailsChange,
                    id: log.revisionId,
                    timestamp: log.updatedAt,
                    entityLabel: "Event",
                    eventTemplateId: event.templateId,
                    fieldChanges: fieldChanges,
                    createdBy: toCreatedBy(log.updatedBy),
                });
            }
        }
    }

    return cards;
}
